import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@ui/accordion.tsx';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@ui/form.tsx';
import { Button } from '@ui/button.tsx';
import { useNavigate } from '@tanstack/react-router';
import { AccordionTriggerLabel } from '@admin/components/workspace/accordion-trigger-label.tsx';
import { RadioGroup, RadioGroupItem } from '@ui/radio-group.tsx';
import { Calendar } from '@ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { cn } from '@r/utils/utils.ts';
import { CalendarIcon } from 'lucide-react';
import { formatISO, format, toDate, add, endOfDay } from 'date-fns';
import { TypologiesTable } from '@admin/components/typologies/typologies-table.tsx';
import { useWorkspaceFormStore } from '@admin/context/workspace-form-context.tsx';
import { toast } from 'sonner';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { Trans, useTranslation } from 'react-i18next';
import { useCreateWorkspace } from '@r/api/generated/client.ts';
import { useAllConfigs, findConfig, getConfigsFromResponse } from '@r/hooks/use-all-configs.ts';
import {
  LogicTypes,
  type LogicType,
  LOGIC_IRIS,
  ALL_LOGIC_IRIS,
  LOGIC_TYPE_TO_IRI,
  isStepLogic,
  isTargetLogic,
  type LogicIri
} from '@admin/types/workspace/logic.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';
import { StepTable } from '@admin/components/logic/step/step-table.tsx';
import { TargetTable } from '@admin/components/logic/target/target-table.tsx';
import { useCallback, useMemo } from 'react';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import type {
  LogicTypeStepWorkspaceWriteJsonMergePatchModel,
  LogicTypeTargetWorkspaceWriteModel,
  TypologyWorkspaceWriteModel
} from '@r/api/generated/client.schemas.ts';
import { Card } from '@r/components/ui/card.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

const POINT_EXPIRATION_RELATIVE_PATTERN =
  /^last day of December this year(?: \+ (\d+) years?)?, 23:59:59$/;

export const CreateWorkspaceStep3 = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();

  const { updateFormData, workspaces } = useWorkspaceFormStore();
  const activeWorkspace = user.data && getActiveWorkspaceUniversal(user.data.visibleWorkspaces);
  const activeWorkspaceId = activeWorkspace?.workspace.id as number;
  const formData = workspaces[activeWorkspaceId];

  const parsePointExpirationDefaults = (rawValue?: string | null) => {
    if (!rawValue) {
      return { type: 'fixed' as const, date: undefined, yearOffset: 0 };
    }

    const match = POINT_EXPIRATION_RELATIVE_PATTERN.exec(rawValue.trim());
    if (match) {
      return {
        type: 'yearEnd' as const,
        date: undefined,
        yearOffset: Number(match[1] ?? 0)
      };
    }

    const parsedDate = new Date(rawValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      return { type: 'fixed' as const, date: parsedDate, yearOffset: 0 };
    }

    return { type: 'fixed' as const, date: undefined, yearOffset: 0 };
  };

  // Fetch de toutes les configs disponibles (mutualisé avec les autres steps)
  const configsQuery = useAllConfigs();
  const configsFromApi = getConfigsFromResponse(configsQuery.data);

  const formSchema = useMemo(() => {
    const logicEnumValues = [...ALL_LOGIC_IRIS] as [string, ...string[]];

    return z
      .object({
        logic: z.enum(logicEnumValues),
        startAt: z.date(),
        endAt: z.date(),
        convertMethod: z.enum(['typology', 'company']),
        pointExpirationType: z.enum(['fixed', 'yearEnd']),
        pointExpirationDate: z.date().optional(),
        pointExpirationYearOffset: z.number().min(0).max(10).optional(),
        typologies: z
          .array(
            z.object({
              name: z.string().max(100),
              pointCoeff: z.number().min(0),
              isDefault: z.boolean()
            })
          )
          .optional(),
        steps: z
          .array(
            z.object({
              name: z.string().max(50),
              description: z.string().nullable().optional(),
              stepPoint: z.number().min(0)
            })
          )
          .optional(),
        targets: z
          .array(
            z.object({
              reference: z.string().max(20),
              description: z.string().nullable().optional(),
              logicTargetType: z
                .enum([LogicLogicReadModelType.TARGET_POINT, LogicLogicReadModelType.TARGET_PERF])
                .optional()
            })
          )
          .optional()
      })
      .superRefine((data, ctx) => {
        const stepsLength = data.steps?.length ?? 0;
        const targetsLength = data.targets?.length ?? 0;
        const typologiesLength = data.typologies?.length ?? 0;
        const typologiesHasDefault =
          data.typologies?.some((typology) => typology.isDefault) ?? false;

        if (isStepLogic(data.logic as LogicIri) && stepsLength === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.logic.step.required'),
            path: ['logic']
          });
        }

        if (isTargetLogic(data.logic as LogicIri) && targetsLength === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.logic.target.required'),
            path: ['logic']
          });
        }

        // Validation : tous les targets doivent avoir le même type
        if (data.targets && data.targets.length > 0) {
          const firstTargetType = data.targets[0]?.logicTargetType;

          // Le premier target doit avoir un type
          if (!firstTargetType) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('form.workspace.target.type.required'),
              path: ['targets', 0, 'logicTargetType']
            });
          } else {
            // Vérifier que tous les targets ont le même type
            const hasMixedTypes = data.targets.some(
              (target) => target.logicTargetType && target.logicTargetType !== firstTargetType
            );

            if (hasMixedTypes) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('form.workspace.target.type.mixed'),
                path: ['targets']
              });
            }
          }
        }

        if (data.convertMethod === 'typology' && typologiesLength === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.convert.method.typology.required'),
            path: ['convertMethod']
          });
        }

        if (data.convertMethod === 'typology' && typologiesLength > 0 && !typologiesHasDefault) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.convert.method.typology.default.required'),
            path: ['convertMethod']
          });
        }

        if (data.startAt && data.endAt && data.startAt >= data.endAt) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.operation.date.start.after.date.end'),
            path: ['endAt']
          });
        }

        if (data.pointExpirationType === 'fixed' && !data.pointExpirationDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.point.expiration.validation.date.required'),
            path: ['pointExpirationDate']
          });
        }

        if (
          data.pointExpirationType === 'yearEnd' &&
          data.pointExpirationYearOffset === undefined
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.point.expiration.validation.year.required'),
            path: ['pointExpirationYearOffset']
          });
        }
      });
  }, [t]);

  type FormSchema = z.infer<typeof formSchema>;

  const pointExpirationDefaults = parsePointExpirationDefaults(
    formData.currentLogicWorkspace?.pointExpirationDate
  );

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      logic: LOGIC_IRIS.STEP,
      startAt: formData.currentLogicWorkspace?.startAt
        ? toDate(formData.currentLogicWorkspace?.startAt)
        : new Date(),
      endAt: formData.currentLogicWorkspace?.endAt
        ? toDate(formData.currentLogicWorkspace?.endAt)
        : add(new Date(), { days: 30 }),
      convertMethod: 'typology',
      pointExpirationType: pointExpirationDefaults.type,
      pointExpirationDate: pointExpirationDefaults.date,
      pointExpirationYearOffset: pointExpirationDefaults.yearOffset,
      typologies: formData.typologies ?? [],
      steps: (formData.currentLogicWorkspace?.steps ?? []).map((step) => ({
        name: step.name,
        stepPoint: step.stepPoint,
        description: step.description ?? undefined
      })),
      targets: formData.currentLogicWorkspace?.targets ?? []
    },
    mode: 'onChange'
  });

  const handleTypologyCreated = async (typology: TypologyWorkspaceWriteModel) => {
    const currentTypologies = form.getValues('typologies') ?? [];
    form.setValue('typologies', [...currentTypologies, typology], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
    form.setValue('convertMethod', 'typology', { shouldDirty: true });
    await form.trigger('convertMethod');
  };

  const handleLogicCreated = useCallback(
    async (
      type: LogicType,
      values:
        | LogicTypeStepWorkspaceWriteJsonMergePatchModel[]
        | (LogicTypeTargetWorkspaceWriteModel & {
            logicTargetType?:
              | typeof LogicLogicReadModelType.TARGET_POINT
              | typeof LogicLogicReadModelType.TARGET_PERF;
          })[]
    ) => {
      if (type === LogicTypes.STEPS) {
        form.setValue(
          type,
          (values as LogicTypeStepWorkspaceWriteJsonMergePatchModel[]).map((step) => ({
            name: step.name ?? '',
            stepPoint: step.stepPoint ?? 0,
            description: step.description ?? undefined
          }))
        );
      } else {
        form.setValue(
          LogicTypes.TARGETS,
          values as (LogicTypeTargetWorkspaceWriteModel & {
            logicTargetType?:
              | typeof LogicLogicReadModelType.TARGET_POINT
              | typeof LogicLogicReadModelType.TARGET_PERF;
          })[]
        );
      }

      if (type === LogicTypes.TARGETS && values.length > 0) {
        const firstTarget = values[0] as LogicTypeTargetWorkspaceWriteModel & {
          logicTargetType?:
            | typeof LogicLogicReadModelType.TARGET_POINT
            | typeof LogicLogicReadModelType.TARGET_PERF;
        };
        if (!firstTarget.logicTargetType) {
          toast.error(t('form.workspace.target.type.required'));
          return;
        }

        const mappedLogic = LOGIC_TYPE_TO_IRI[firstTarget.logicTargetType];

        form.setValue('logic', mappedLogic, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
        await form.trigger('logic');
        return;
      }

      form.setValue('logic', LOGIC_IRIS.STEP, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
      await form.trigger('logic');
    },
    [form]
  );

  const startAt = form.watch('startAt');
  const endAt = form.watch('endAt');
  const pointExpirationType = form.watch('pointExpirationType');

  const navigate = useNavigate();

  const submitMutation = useCreateWorkspace({
    mutation: {
      onSuccess: async () => {
        await navigate({ to: '/create-workspace/step/4' });
      },
      onError: (error: unknown) => {
        if (isOrvalErrorFromPath(error, 'put', '/admin/workspaces')) {
          showOrvalErrorToast(error);
          return;
        }

        toast.error(t('form.workspace.unknown.error'));
      }
    }
  });

  async function performSubmit(values: FormSchema) {
    if (!configsFromApi?.length) {
      toast.error(t('form.workspace.configs.not.loaded'));
      return;
    }

    // Trouver la config pts-conversion-type
    const ptsConversionConfig = findConfig(configsFromApi, 'core', 'pts-conversion-type');

    if (!ptsConversionConfig?.id) {
      toast.error(t('form.workspace.configs.not.found'));
      return;
    }

    // Nettoyer les targets : supprimer logicTargetType avant l'envoi
    const cleanedTargets = values.targets?.map(({ logicTargetType, ...target }) => target) || [];
    const yearOffset = values.pointExpirationYearOffset ?? 0;
    const yearUnit = yearOffset === 1 ? 'year' : 'years';
    const pointExpirationDate =
      values.pointExpirationType === 'yearEnd'
        ? yearOffset > 0
          ? `last day of December this year + ${yearOffset} ${yearUnit}, 23:59:59`
          : 'last day of December this year, 23:59:59'
        : values.pointExpirationDate
          ? formatISO(endOfDay(values.pointExpirationDate))
          : 'last day of December this year, 23:59:59';

    const newFormData = await updateFormData(activeWorkspaceId as number, {
      currentLogicWorkspace: {
        startAt: formatISO(values.startAt),
        endAt: formatISO(values.endAt),
        steps: isStepLogic(values.logic as LogicIri) ? values.steps : [],
        targets: values.targets && values.targets.length > 0 ? cleanedTargets : [],
        logic: values.logic,
        pointExpirationDate
      },
      workspaceConfigs: [
        {
          value: `pt-conversion-${values.convertMethod}`,
          config: `/configs/${ptsConversionConfig.id}`
        }
      ],
      typologies: values.convertMethod === 'typology' && values.typologies ? values.typologies : []
    });
    submitMutation.mutate({
      id: String(activeWorkspaceId),
      data: newFormData as unknown as import('@r/api/generated/client.schemas.ts').WorkspaceWorkspaceWriteModel
    });
  }

  async function onSubmit(values: FormSchema) {
    await performSubmit(values);
  }

  const handleLogicChange = async (value: string) => {
    const currentTargets = form.getValues('targets') ?? [];

    if (isTargetLogic(value as LogicIri) && currentTargets.length > 0) {
      const firstTarget = currentTargets[0];
      if (firstTarget?.logicTargetType) {
        const targetLogicIri = LOGIC_TYPE_TO_IRI[firstTarget.logicTargetType];
        form.setValue('logic', targetLogicIri, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
        return;
      }
    }

    form.setValue('logic', value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-0.5">
        <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
          <AccordionItem value="item-1" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['logic']}
                label={t('form.workspace.logic')}
                form={form}
              />
            </AccordionTrigger>

            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="logic"
                  render={({ field }) => (
                    <FormItem className="space-y-3 w-full">
                      <FormDescription>{t('form.workspace.logic.description')}</FormDescription>
                      <FormControl>
                        <div>
                          <div className="grid gap-4 py-4">
                            <RadioGroup
                              onValueChange={handleLogicChange}
                              value={
                                isTargetLogic(field.value as LogicIri)
                                  ? LOGIC_IRIS.TARGET_PERF
                                  : field.value
                              }
                              className="grid-flow-col"
                            >
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value={LOGIC_IRIS.STEP} />
                                </FormControl>

                                <FormLabel className="font-normal">
                                  {t('form.workspace.logic.step')}
                                </FormLabel>
                              </FormItem>

                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value={LOGIC_IRIS.TARGET_PERF} />
                                </FormControl>

                                <FormLabel className="font-normal">
                                  {t('form.workspace.logic.target')}
                                </FormLabel>
                              </FormItem>

                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value={LOGIC_IRIS.LINEAR} />
                                </FormControl>

                                <FormLabel className="font-normal">
                                  {t('form.workspace.logic.linear')}
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </div>

                          {isStepLogic(field.value as LogicIri) && (
                            <StepTable
                              data={form.getValues('steps') ?? []}
                              setData={handleLogicCreated}
                            />
                          )}

                          {isTargetLogic(field.value as LogicIri) && (
                            <TargetTable
                              data={form.getValues('targets') ?? []}
                              setData={handleLogicCreated}
                            />
                          )}
                        </div>
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>{' '}
          <AccordionItem value="item-2" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['startAt', 'endAt']}
                label={t('form.workspace.operation.date')}
                form={form}
              />
            </AccordionTrigger>

            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <span className="mb-2 mt-1 font-light peer-disabled:cursor-not-allowed peer-disabled:opacity-30 text-foreground text-[0.875rem] pl-[0.125rem]">
                {t('form.workspace.operation.date.description')}
              </span>

              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="startAt"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('form.workspace.operation.date.start')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={'outline'}
                              className={cn(
                                'w-[240px] pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                              <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-auto" align="start">
                          <Calendar
                            mode="single"
                            required={true}
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => {
                              const startOfToday = new Date();
                              startOfToday.setHours(0, 0, 0, 0);
                              return date < startOfToday || date >= endAt;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endAt"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('form.workspace.operation.date.end')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={'outline'}
                              className={cn(
                                'w-[240px] pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                              <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-auto" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => !startAt || date < startAt}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>{' '}
          <AccordionItem value="item-3" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['convertMethod']}
                label={t('form.workspace.convert.method.label')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="convertMethod"
                  render={({ field }) => (
                    <FormItem className="space-y-3 w-full">
                      <FormLabel>{t('form.workspace.convert.method')}</FormLabel>
                      <FormDescription>
                        {t('form.workspace.convert.method.description')}
                      </FormDescription>
                      <FormControl>
                        <div>
                          <div className="grid gap-4 py-4">
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="grid-flow-col"
                            >
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="typology" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {t('form.workspace.convert.method.choice.typology')}
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="company" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {t('form.workspace.convert.method.choice.company')}
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </div>
                          {field.value === 'typology' && (
                            <div>
                              <FormLabel>{t('form.workspace.convert.method.typology')}</FormLabel>
                              <FormDescription>
                                <Trans
                                  i18nKey={t('form.workspace.convert.method.typology.description')}
                                  components={{ 1: <br /> }}
                                />
                              </FormDescription>
                              <TypologiesTable
                                data={form.getValues('typologies') ?? []}
                                onCreateSuccess={handleTypologyCreated}
                                useLocalState={true}
                                createdAt={false}
                              />
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-4" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={[
                  'pointExpirationType',
                  'pointExpirationDate',
                  'pointExpirationYearOffset'
                ]}
                label={t('form.workspace.point.expiration.title')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <div className="flex flex-row space-x-4 w-full">
                <div className="space-y-3 w-full">
                  <FormDescription>
                    {t('form.workspace.point.expiration.description')}
                  </FormDescription>
                  <FormField
                    control={form.control}
                    name="pointExpirationType"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <div className="grid gap-4 py-4">
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="grid-flow-col"
                          >
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="fixed" />
                              </FormControl>

                              <FormLabel className="font-normal">
                                {t('form.workspace.point.expiration.option.fixed')}
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="yearEnd" />
                              </FormControl>

                              <FormLabel className="font-normal">
                                {t('form.workspace.point.expiration.option.year.end')}
                              </FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </div>
                      </FormItem>
                    )}
                  />

                  {pointExpirationType === 'fixed' && (
                    <FormField
                      control={form.control}
                      name="pointExpirationDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>{t('form.workspace.point.expiration.date.label')}</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={'outline'}
                                  className={cn(
                                    'w-[240px] pl-3 text-left font-normal',
                                    !field.value && 'text-muted-foreground'
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, 'PPP')
                                  ) : (
                                    <span>
                                      {t('form.workspace.point.expiration.date.placeholder')}
                                    </span>
                                  )}
                                  <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 w-auto" align="start">
                              <Calendar
                                mode="single"
                                required={true}
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => {
                                  const startOfToday = new Date();
                                  startOfToday.setHours(0, 0, 0, 0);
                                  return date < startOfToday;
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {pointExpirationType === 'yearEnd' && (
                    <FormField
                      control={form.control}
                      name="pointExpirationYearOffset"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>{t('form.workspace.point.expiration.date.label')}</FormLabel>
                          <Select
                            onValueChange={(value) => field.onChange(Number(value))}
                            value={field.value !== undefined ? String(field.value) : ''}
                          >
                            <FormControl>
                              <SelectTrigger className="w-[240px]">
                                <SelectValue
                                  placeholder={t(
                                    'form.workspace.point.expiration.select.placeholder'
                                  )}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Array.from({ length: 11 }, (_, index) => index).map((offset) => (
                                <SelectItem key={offset} value={String(offset)}>
                                  {offset === 0
                                    ? t('form.workspace.point.expiration.select.option.n')
                                    : t('form.workspace.point.expiration.select.option.n.plus', {
                                        offset
                                      })}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  <Card className="p-4 space-y-1 bg-purple-200">
                    <p className="font-bold text-purple-600">
                      {t('form.workspace.point.expiration.notice.title')}
                    </p>
                    <p className="italic text-purple-600">
                      {t('form.workspace.point.expiration.notice.example.line1')}
                      <br />
                      {t('form.workspace.point.expiration.notice.example.line2')}
                    </p>
                  </Card>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Button
          type="submit"
          rounded="full"
          loading={form.formState.isSubmitting || submitMutation.isPending}
          disabled={!configsFromApi?.length || configsQuery.isPending}
        >
          {t('form.workspace.next.step')}
        </Button>
      </form>
    </Form>
  );
};
